import { type FC, useEffect, useRef } from 'react';

import { useHistory } from 'react-router-dom';

import { DashboardEvent } from 'features/dashboard/lib/constants';
import { ModalType } from 'shared/lib/analytics/modalTypes';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { urls } from 'shared/urls';

import { UpgradePromoEvent } from '../lib/constants';
import { usePromoStore } from '../lib/usePromoStore';
import { useUpgradeTrigger } from '../lib/useUpgradeTrigger';
import { UpgradePromoBanner } from './UpgradePromoBanner';
import { UpgradePromoModal } from './UpgradePromoModal';
import { UpgradePromoToast } from './UpgradePromoToast';

/**
 * Mount-once orchestrator for the upgrade-plan promo (CL-6252). Reads the
 * eligibility decision from `useUpgradeTrigger`, renders the chosen layout,
 * fires the `SHOWN` event the first time the user becomes eligible per
 * session, and routes CTA / Dismiss / Maybe-later actions through analytics
 * + the persisted promo store.
 */
export const UpgradePromo: FC = () => {
  const decision = useUpgradeTrigger();
  const { recordDismissal, recordDemotion, recordVariantBucket, variantBucket } = usePromoStore();
  const history = useHistory();
  const shownRef = useRef(false);

  useEffect(() => {
    if (decision.eligible) {
      // Persist sticky variant on first eligibility so funnel attribution
      // survives PostHog flag config changes.
      if (!variantBucket && (decision.layout === 'modal' || decision.layout === 'toast')) {
        recordVariantBucket(decision.layout);
      }
      if (!shownRef.current) {
        shownRef.current = true;
        trackEvent(UpgradePromoEvent.SHOWN, {
          layout: decision.layout,
          target_tier: decision.pitch.targetTier,
        });
        // Mirrors `useTrackedShowModal` for parity with other modal events.
        if (decision.layout === 'modal') {
          trackEvent(DashboardEvent.POPUP_SHOW, { type: ModalType.UPGRADE_PROMO });
        }
      }
    } else {
      shownRef.current = false;
      trackEvent(UpgradePromoEvent.ELIGIBILITY_EVALUATED, { reason: decision.reason });
    }
  }, [decision, recordVariantBucket, variantBucket]);

  if (!decision.eligible) return null;

  // CTA, View-all-plans, and Dismiss all bump the dismissal cooldown so the
  // promo doesn't follow the user around. The cadence (`INDIVIDUAL_REPROMPT_DAYS`)
  // controls how soon they see it again.
  const handleCta = () => {
    trackEvent(UpgradePromoEvent.CTA_CLICK, {
      layout: decision.layout,
      target_tier: decision.pitch.targetTier,
    });
    recordDismissal();
    history.push(urls.subscriptionChangePlan.url());
  };

  const handleMaybeLater = () => {
    trackEvent(UpgradePromoEvent.DEMOTED_TO_BANNER, {
      layout: decision.layout,
      target_tier: decision.pitch.targetTier,
    });
    recordDemotion();
  };

  const handleDismiss = () => {
    trackEvent(UpgradePromoEvent.DISMISSED, {
      layout: decision.layout,
      target_tier: decision.pitch.targetTier,
    });
    recordDismissal();
  };

  const handleViewAllPlans = () => {
    trackEvent(UpgradePromoEvent.VIEW_ALL_PLANS, {
      layout: decision.layout,
      target_tier: decision.pitch.targetTier,
    });
    recordDismissal();
    history.push(urls.subscriptionChangePlan.url());
  };

  const layoutProps = {
    pitch: decision.pitch,
    onCta: handleCta,
    onMaybeLater: handleMaybeLater,
    onDismiss: handleDismiss,
    onViewAllPlans: handleViewAllPlans,
  };

  if (decision.layout === 'banner') return <UpgradePromoBanner {...layoutProps} />;
  if (decision.layout === 'toast') return <UpgradePromoToast {...layoutProps} />;
  return <UpgradePromoModal {...layoutProps} />;
};
